import React from 'react';
import { TextField, Box, alpha, useTheme, Typography, IconButton, useMediaQuery } from '@mui/material';
import { Search } from 'lucide-react';

const InlineGlobalSearch = ({ onFocus, onClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const shortcutKey = isMac ? '⌘K' : 'Ctrl+K';

  // Mobile: Show only search button
  if (isMobile) {
    return (
      <IconButton
        onClick={onClick}
        onFocus={onFocus}
        sx={{
          bgcolor: '#ffffff',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          borderRadius: '8px',
          width: '40px',
          height: '40px',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
        }}
      >
        <Search 
          size={18} 
          style={{ 
            color: alpha(theme.palette.text.secondary, 0.7),
          }} 
        />
      </IconButton>
    );
  }

  // Desktop: Show full search input
  return (
    <TextField
      fullWidth
      placeholder="Rechercher"
      onClick={onClick}
      readOnly
      InputProps={{
        startAdornment: (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5 }}>
            <Search 
              size={18} 
              style={{ 
                color: alpha(theme.palette.text.secondary, 0.6),
              }} 
            />
          </Box>
        ),
        endAdornment: (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              ml: 1,
              px: 1,
              py: 0.5,
              borderRadius: '4px',
              bgcolor: alpha(theme.palette.grey[100], 0.8),
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '11px',
                fontWeight: 500,
                color: alpha(theme.palette.text.secondary, 0.6),
                fontFamily: 'monospace',
                letterSpacing: '0.5px',
              }}
            >
              {shortcutKey}
            </Typography>
          </Box>
        ),
      }}
      sx={{
        maxWidth: { xs: '100%', sm: '400px', md: '500px' },
        '& .MuiOutlinedInput-root': {
          fontSize: { xs: '14px', sm: '14px' },
          borderRadius: { xs: '8px', sm: '8px' },
          bgcolor: '#ffffff',
          transition: 'all 0.2s ease',
          color: theme.palette.text.primary,
          minHeight: { xs: '40px', sm: '44px' },
          height: { xs: '40px', sm: '44px' },
          '& fieldset': {
            borderWidth: '1px',
            borderColor: alpha(theme.palette.divider, 0.2),
          },
          '&:hover': {
            bgcolor: '#ffffff',
            '& fieldset': {
              borderColor: alpha(theme.palette.divider, 0.3),
            },
          },
          '&.Mui-focused': {
            bgcolor: '#ffffff',
            '& fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.5),
              borderWidth: '1px',
            },
          },
        },
        '& .MuiOutlinedInput-input': {
          py: { xs: 1, sm: 1.25 },
          fontSize: { xs: '14px', sm: '14px' },
          color: theme.palette.text.primary,
          '&::placeholder': {
            color: alpha(theme.palette.text.secondary, 0.6),
            opacity: 1,
            fontSize: { xs: '14px', sm: '14px' },
          },
        },
      }}
    />
  );
};

export default InlineGlobalSearch;

